// ─── PERSONA OPTIONS ────────────────────────────────────────────────────────
// Fallback ONLY. The live list comes from GET /api/v1/context/personas via
// useContextOptions(); this array is what the form shows before that fetch
// lands or when it fails.
//
// ⚠️ It carries NO `enabled` notion — the backend filters disabled personas out
// of the live listing, and this fallback cannot. So a failed fetch can re-offer
// a persona the tenant's admin switched off, and submitting it comes back as
// 422 BRIEF_VALIDATION_FAILED (`field: "persona"`), which useGeneration words as
// "reload the page". Keep the two in step: this is the current global set (9 B2B
// + 4 B2C); it replaced a stale 6-entry list that predated the 2026-09-06
// redesign.
export const PERSONA_OPTIONS = [
  // B2B — business roles
  { value: 'ceo-founder-smb',                 label: 'AD / CEO / Imprenditore' },
  { value: 'cfo-finance-leader',              label: 'Responsabile Amministrativo / CFO' },
  { value: 'cmo-marketing-leader',            label: 'Responsabile Marketing e Comunicazione / CMO' },
  { value: 'coo-production-leader',           label: 'Responsabile Produzione / COO' },
  { value: 'cto-technical-leader',            label: 'Responsabile Tecnologie / CTO' },
  { value: 'hr-people-leader',                label: 'Responsabile Risorse Umane / HR' },
  { value: 'procurement-supply-chain-leader', label: 'Responsabile Acquisti / Supply Chain' },
  { value: 'rnd-engineering-leader',          label: 'Responsabile Progettazione / R&S' },
  { value: 'sales-revenue-leader',            label: 'Responsabile Sales / Account Manager' },
  // B2C — audience segments
  { value: 'baby-boomers',                    label: 'Baby Boomer (1946–1964)' },
  { value: 'generation-x',                    label: 'Generazione X (1965–1980)' },
  { value: 'millennials',                     label: 'Millennials (1981–1996)' },
  { value: 'generation-z',                    label: 'Generazione Z (1997–2012)' },
];

// ─── TONE OPTIONS ───────────────────────────────────────────────────────────
// Fallback only. The live list comes from GET /api/v1/context/tone-presets via
// useContextOptions(); this array is what the form shows before the fetch lands
// or when it fails. Labels here are ALSO the legacy label→slug map used by
// toneMigration.toneStateFromParams for examples authored with human labels.

// Frontend-only escape hatch: never served by the backend, never sent as a
// literal (resolveToneHint swaps it for the raw customTone text).
export const CUSTOM_TONE_OPTION = { value: 'custom', label: 'Personalizzato*' };

export const TONE_OPTIONS = [
  { value: 'professional-conversational', label: 'Professional Conversational' },
  { value: 'empathetic-inspiring',        label: 'Empathetic Inspiring' },
  { value: 'educational-authoritative',   label: 'Educational Authoritative' },
  { value: 'witty-smart',                 label: 'Witty Smart' },
  { value: 'formal-institutional',        label: 'Formal Institutional' },
  { value: 'bold-provocative',            label: 'Bold & Provocative' },
  { value: 'brand-voice',                 label: 'Brand Voice (da KB)' },
  CUSTOM_TONE_OPTION,
];

// ─── OBJECTIVE OPTIONS ──────────────────────────────────────────────────────
export const OBJECTIVE_OPTIONS = [
  { value: 'brand-awareness',      label: 'Brand Awareness' },
  { value: 'customer-engagement',  label: 'Customer Engagement' },
  { value: 'drive-traffic',        label: 'Drive Traffic' },
  { value: 'increase-sales',       label: 'Increase Sales' },
  { value: 'lead-generation',      label: 'Lead Generation' },
  { value: 'product-launch',       label: 'Product Launch' },
  { value: 'recruitment',          label: 'Recruitment' },
  { value: 'thought-leadership',   label: 'Thought Leadership' },
];

// ─── LINKEDIN OPTIONS ────────────────────────────────────────────────────────
export const POST_LENGTH_OPTIONS = [
  { value: 'short',  label: 'Short (80-130 parole)' },
  { value: 'medium', label: 'Medium (130-180 parole)' },
  { value: 'long',   label: 'Long (180-250 parole)' },
];

export const CTA_TYPE_OPTIONS = [
  { value: 'soft',   label: 'Soft (domanda aperta)' },
  { value: 'medium', label: 'Medium (link in comments)' },
  { value: 'hard',   label: 'Hard (CTA diretta)' },
];

export const CAROUSEL_SLIDE_OPTIONS = [
  { value: '6',  label: '6 slide (quick tips)' },
  { value: '8',  label: '8 slide (standard)' },
  { value: '10', label: '10 slide (deep-dive)' },
];

// ─── BLOG OPTIONS ────────────────────────────────────────────────────────────
export const BLOG_LENGTH_OPTIONS = [
  { value: '400-600',   label: '400-600 (news / glossary)' },
  { value: '800-1200',  label: '800-1200 (quick guide)' },
  { value: '1500-2000', label: '1500-2000 (standard SEO)' },
  { value: '2500-3500', label: '2500-3500 (pillar content)' },
];

export const SEO_INTENT_OPTIONS = [
  { value: 'informational',  label: 'Informational (how to, what is)' },
  { value: 'commercial',     label: 'Commercial (best, comparison)' },
  { value: 'transactional',  label: 'Transactional (buy, book)' },
];

// ─── EMAIL OPTIONS ───────────────────────────────────────────────────────────
export const EMAIL_LENGTH_OPTIONS = [
  { value: 'breve',        label: 'Breve (300 parole)' },
  { value: 'standard',     label: 'Standard (500 parole)' },
  { value: 'approfondita', label: 'Approfondita (800 parole)' },
];

// ─── SUBJECT STYLE OPTIONS (email-classic) ───────────────────────────────────
export const SUBJECT_STYLE_OPTIONS = [
  { value: 'curiosity', label: 'Curiosità' },
  { value: 'urgency',   label: 'Urgenza' },
  { value: 'personal',  label: 'Personale' },
  { value: 'value',     label: 'Valore' },
];

// ─── ARTICLE STYLE OPTIONS (linkedin-newsletter) ─────────────────────────────
export const ARTICLE_STYLE_OPTIONS = [
  { value: 'thought-leader', label: 'Opinione / Influenza intellettuale' },
  { value: 'how-to',         label: 'Guida pratica' },
  { value: 'trend-analysis', label: 'Analisi di tendenza' },
  { value: 'storytelling',   label: 'Narrazione' },
];

// ─── ADS OPTIONS ─────────────────────────────────────────────────────────────
export const ADS_CHANNEL_OPTIONS = [
  { value: 'google-search',  label: 'Google Search Ads' },
  { value: 'google-display', label: 'Google Display Ads' },
  { value: 'meta',           label: 'Meta Ads (Facebook/Instagram)' },
  { value: 'linkedin',       label: 'LinkedIn Ads' },
];

// ─── CASE STUDY OPTIONS ──────────────────────────────────────────────────────
export const CASE_STUDY_SECTION_OPTIONS = [
  { value: 'standard',  label: 'Standard (sintesi efficace)' },
  { value: 'detailed',  label: 'Dettagliato (approfondimenti)' },
];

export const CASE_STUDY_FOCUS_OPTIONS = [
  { value: 'results',    label: 'Risultati e ROI' },
  { value: 'process',    label: 'Processo e metodologia' },
  { value: 'innovation', label: 'Innovazione' },
];

export const LANDING_HEADLINE_OPTIONS = [
  { value: 'problem-solution', label: 'Problem-Solution' },
  { value: 'benefit-driven',   label: 'Benefit-Driven' },
  { value: 'question-based',   label: 'Question-Based' },
];
